import React, { useState, useEffect, useRef } from 'react';
import './GuestDetailCard.scss';
import '../../components/UtilityForm/UtilityForm.scss';
import cn from 'classnames';
import { ReactComponent as RemoveIcon } from 'assets/ico-remove.svg';
import { useWindow } from 'hooks/useWindow';
import { calendarService } from '../../services/calendarService';

// owner-stay-and-platforms-links

const GuestDetailCard = ({ onChange, showErrors, initialGuests }) => {
  const [guests, setGuests] = useState(
    initialGuests && initialGuests.length > 0
      ? initialGuests
      : [{ id: 1, fullName: '', idFile: null }]
  );
  const [activeIndex, setActiveIndex] = useState(null);
  const smallScrnpxl = 800;
  const { width } = useWindow();
  const autoOpened = useRef(false);

  useEffect(() => {
    if (
      initialGuests &&
      initialGuests.length > 0 &&
      JSON.stringify(initialGuests) !== JSON.stringify(guests)
    ) {
      setGuests(initialGuests);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialGuests]);

  useEffect(() => {
    if (onChange) {
      onChange(guests);
    }
  }, [guests, onChange]);

  useEffect(() => {
    if (!autoOpened.current && width <= smallScrnpxl && guests && guests.length > 0) {
      setActiveIndex(4);
      autoOpened.current = true;
    }
  }, [width, smallScrnpxl, guests]);

  const handleClick = (index) => {
    setActiveIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  const handleButtonClick = (id) => {
    document.getElementById(id).click();
  };

  const addGuest = () => {
    const maxId =
      guests.length > 0
        ? Math.max(...guests.map((g) => (Number.isNaN(Number(g.id)) ? 0 : Number(g.id))))
        : 0;
    const newId = maxId + 1;
    setGuests([...guests, { id: newId, fullName: '', idFile: null }]);
  };

  const removeGuest = (id) => {
    if (guests.length > 1) {
      setGuests(guests.filter((guest) => guest.id !== id));
    }
  };

  const handleInputChange = (guestId, field, value) => {
    setGuests(guests.map((guest) => (guest.id === guestId ? { ...guest, [field]: value } : guest)));
  };

  const handleFileChange = async (guestId, field, file) => {
    if (!file) return;

    setGuests((prevGuests) =>
      prevGuests.map((guest) => (guest.id === guestId ? { ...guest, [field]: file } : guest))
    );

    const documentType = 'passport';

    // Set loading state for validation
    setGuests((prevGuests) =>
      prevGuests.map((g) =>
        g.id === guestId
          ? {
              ...g,
              validationStatus: {
                ...(g.validationStatus || {}),
                [field]: { loading: true, error: null },
              },
            }
          : g
      )
    );

    try {
      const validationPayload = [{ documentType, file }];
      const result = await calendarService.ValidateGuestDoc(validationPayload);

      setGuests((prevGuests) =>
        prevGuests.map((g) =>
          g.id === guestId
            ? {
                ...g,
                idType: result.idType || result.docType || 'PASSPORT',
                validationStatus: {
                  ...(g.validationStatus || {}),
                  [field]: {
                    loading: false,
                    error: result.valid
                      ? null
                      : result.error || result.message || 'Invalid document',
                    success: result.valid,
                  },
                },
              }
            : g
        )
      );
    } catch (error) {
      setGuests((prevGuests) =>
        prevGuests.map((g) =>
          g.id === guestId
            ? {
                ...g,
                validationStatus: {
                  ...(g.validationStatus || {}),
                  [field]: { loading: false, error: 'Validation failed', success: false },
                },
              }
            : g
        )
      );
    }
  };

  const renderValidationError = (isVisible, message = 'This field is required') =>
    isVisible && showErrors ? (
      <div className="invalid-field" style={{ fontSize: '12px', marginTop: '2px' }}>
        {message}
      </div>
    ) : null;

  const getFileName = (file) => {
    if (!file) return '';
    if (typeof file === 'string') {
      // If it's a URL or path, get the last part
      const parts = file.split(/[\\/]/);

      return parts[parts.length - 1];
    }

    return file.name;
  };

  return (
    <div className="utility-form-container">
      <div className={cn('form-card-wrapper', 'utility-questions-wrapper')}>
        {guests.map((guest, index) => {
          const fullNameId = `fullName-${guest.id}`;
          // const idTypeId = `idType-${guest.id}`;
          // const isEmiratesId = guest.idType === 'emirates_id';

          return (
            <div key={guest.id} className="form-card">
              {guests.length > 1 && (
                <p onClick={() => removeGuest(guest.id)} className="remove-icon-wrapper">
                  <RemoveIcon />
                </p>
              )}
              <div className="question-plus-minus-wrapper">
                <h3
                  className={cn('form-card-title')}
                  onClick={() => width <= smallScrnpxl && handleClick(index + 4)}
                >
                  {width <= smallScrnpxl ? (
                    <div
                      className={cn('plusminus', 'expand-collapse', {
                        active: activeIndex === index + 4,
                      })}
                    />
                  ) : (
                    <></>
                  )}
                  <p>{`Guest - ${index + 1}`}</p>
                </h3>
              </div>
              <div className={cn('answer', { opened: activeIndex === index + 4 })}>
                <div className="form-group">
                  <label className="form-label" htmlFor={fullNameId}>
                    Full Name
                  </label>
                  <input
                    id={fullNameId}
                    type="text"
                    value={guest.fullName}
                    onChange={(e) => handleInputChange(guest.id, 'fullName', e.target.value)}
                    className="form-input form-control"
                  />
                  {renderValidationError(!guest.fullName)}
                  {renderValidationError(
                    !!guest.fullName && !guest.fullName.trim().includes(' '),
                    'Please enter full name'
                  )}
                </div>

                <div className="form-group">
                  <div className="d-flex align-items-center mb-1">
                    <button
                      type="button"
                      className="info-btn "
                      title="Upload Passport or Emirates Id"
                    >
                      i
                    </button>
                    <label className="form-label mb-0 ml-2" htmlFor={`idFile-${guest.id}`}>
                      Upload Passport or Emirates Id
                    </label>
                  </div>
                  <button
                    type="button"
                    className={cn('file-input-button', 'form-control')}
                    onClick={() => handleButtonClick(`idFile-${guest.id}`)}
                    disabled={guest.validationStatus?.idFile?.loading}
                  >
                    <input
                      id={`idFile-${guest.id}`}
                      type="file"
                      hidden
                      onChange={(e) => handleFileChange(guest.id, 'idFile', e.target.files[0])}
                      accept="image/*, application/pdf"
                      disabled={guest.validationStatus?.idFile?.loading}
                    />
                    Choose file
                  </button>
                  {guest.idFile ? (
                    <p
                      className={cn('file-name', {
                        'file-selected': guest.validationStatus?.idFile?.success,
                      })}
                      style={guest.validationStatus?.idFile?.error ? { color: 'red' } : {}}
                    >
                      {getFileName(guest.idFile)}
                    </p>
                  ) : (
                    <p className="no-file-text">No file chosen</p>
                  )}
                  {guest.validationStatus?.idFile?.loading && (
                    <div className="text-info" style={{ fontSize: '12px' }}>
                      Validating document...
                    </div>
                  )}
                  {guest.validationStatus?.idFile?.error && (
                    <div className="invalid-field" style={{ fontSize: '12px' }}>
                      {guest.validationStatus.idFile.error}
                    </div>
                  )}
                  {renderValidationError(!guest.idFile)}
                </div>
              </div>
            </div>
          );
        })}
        <div className="add-utility-btn-wrapper">
          <button className="btn" type="button" onClick={addGuest}>
            + Add another Guest
          </button>
        </div>
      </div>
    </div>
  );
};

export default GuestDetailCard;
